import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Coins, Loader2, MessageSquare, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, rupiah, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { RequestInfo } from "@/components/RequestInfo";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { DatePicker } from "@/components/DatePicker";
import { PageLoader } from "@/components/layout/RequireAuth";

const NoCredit = () => (
  <div className="space-y-3 rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="insufficient-credit-alert">
    <p className="text-sm font-semibold text-rose-800">Kredit Anda tidak cukup</p>
    <p className="text-xs text-rose-700">Anda membutuhkan kredit untuk mengirim penawaran.</p>
    <Button asChild className="w-full rounded-xl bg-stone-900 hover:bg-stone-800" data-testid="buy-credit-button"><Link to="/dasbor/kredit#beli">Beli Kredit</Link></Button>
  </div>
);

const MyQuote = ({ data }) => (
  <div className="space-y-4" data-testid="my-quote-panel">
    <div className="flex items-center justify-between">
      <p className="font-bold text-stone-900">Penawaran Anda</p>
      <StatusBadge status={data.my_quote.status} testId="my-quote-status" />
    </div>
    <p className="text-3xl font-extrabold text-stone-900">{rupiah(data.my_quote.price)}</p>
    <p className="text-sm text-stone-600">{data.my_quote.duration}{data.my_quote.work_date && ` · mulai ${formatDate(data.my_quote.work_date)}`}</p>
    <p className="whitespace-pre-line rounded-xl bg-stone-50 p-3 text-sm text-stone-700">{data.my_quote.message}</p>
    {data.my_quote.status === "diterima" && <WhatsAppButton whatsapp={data.whatsapp} testId="lead-whatsapp-button" fallback="Pelanggan belum menambahkan nomor WhatsApp." />}
    {data.thread_id && (
      <Button asChild variant="outline" className="w-full rounded-xl" data-testid="lead-chat-button">
        <Link to={`/pesan/${data.thread_id}`}><MessageSquare className="mr-2 h-4 w-4" /> Buka chat</Link>
      </Button>
    )}
  </div>
);

const QuoteForm = ({ data, onSent }) => {
  const [f, setF] = useState({ price: "", duration: "", work_date: "", message: "" });
  const [sending, setSending] = useState(false);
  const noCredit = data.credits < data.credits_per_quote;
  const [forceNoCredit, setForceNoCredit] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: k === "price" ? e.target.value.replace(/\D/g, "") : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/requests/${data.request_id}/quotes`, { ...f, price: Number(f.price), work_date: f.work_date || null });
      toast.success("Penawaran terkirim. 1 kredit digunakan.");
      onSent();
    } catch (err) {
      if (err.response?.status === 402) setForceNoCredit(true);
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="quote-form">
      <p className="font-bold text-stone-900">Kirim penawaran</p>
      <p className="flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="quote-cost-info">
        <Coins className="h-4 w-4" /> Biaya {data.credits_per_quote} kredit · Saldo Anda {data.credits} kredit
      </p>
      {(noCredit || forceNoCredit) && <NoCredit />}
      <div className="space-y-2">
        <Label>Harga penawaran</Label>
        <div className="flex items-center rounded-xl border border-input bg-white pl-3 focus-within:ring-2 focus-within:ring-teal-600/40">
          <span className="text-sm font-semibold text-stone-500">Rp</span>
          <input inputMode="numeric" required value={f.price ? Number(f.price).toLocaleString("id-ID") : ""} onChange={set("price")}
            className="h-11 w-full bg-transparent px-2 text-sm focus:outline-none" placeholder="150.000" data-testid="quote-price-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Estimasi durasi</Label>
        <Input required value={f.duration} onChange={set("duration")} placeholder="mis. 1 hari, 3–4 jam" className="h-11 rounded-xl" data-testid="quote-duration-input" />
      </div>
      <div className="space-y-2">
        <Label>Tanggal bisa mulai (opsional)</Label>
        <DatePicker value={f.work_date} onChange={(work_date) => setF({ ...f, work_date })} testId="quote-workdate-input" />
      </div>
      <div className="space-y-2">
        <Label>Pesan untuk pelanggan</Label>
        <Textarea required minLength={10} rows={4} value={f.message} onChange={set("message")} placeholder="Jelaskan apa yang termasuk dalam harga…" className="rounded-xl" data-testid="quote-message-input" />
      </div>
      <Button type="submit" disabled={sending || noCredit || forceNoCredit} className="h-11 w-full rounded-xl bg-teal-700 font-semibold hover:bg-teal-800" data-testid="quote-submit-button">
        {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Kirim Penawaran
      </Button>
    </form>
  );
};

export default function LeadDetail() {
  const { id } = useParams();
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get(`/provider/leads/${id}`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e))), [id]);
  useEffect(() => {
    load();
  }, [load]);

  if (error) return <div className="container-page py-16 text-center text-stone-500" data-testid="lead-error">{error}</div>;
  if (!data) return <PageLoader />;

  return (
    <div className="container-page py-10 lg:py-14" data-testid="lead-detail-page">
      <Link to="/dasbor/permintaan-masuk" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-teal-700" data-testid="back-to-leads">
        <ArrowLeft className="h-4 w-4" /> Permintaan Masuk
      </Link>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl" data-testid="lead-detail-title">{data.category_name}</h1>
        <StatusBadge status={data.status} testId="lead-detail-status" />
        <span className="chip" data-testid="lead-quote-count">{data.quote_count}/{data.max_quotes} penawaran</span>
      </div>
      <p className="mt-2 text-sm text-stone-500">Dari {data.customer_name}</p>
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_380px]">
        <div className="card-soft p-6 sm:p-8">
          <RequestInfo req={data} showAddress={!!data.address} />
          {!data.address && (
            <p className="mt-6 flex items-center gap-2 text-xs text-stone-500"><Lock className="h-3.5 w-3.5" /> Alamat lengkap & kontak pelanggan tampil setelah penawaran Anda diterima.</p>
          )}
        </div>
        <aside className="card-soft self-start p-6">
          {data.my_quote ? <MyQuote data={data} /> : data.can_quote ? (
            <QuoteForm data={data} onSent={() => { load(); refreshUser(); }} />
          ) : (
            <p className="text-sm text-stone-600" data-testid="lead-blocked-reason">{data.blocked_reason}</p>
          )}
        </aside>
      </div>
    </div>
  );
}
